import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { listCustomerApiKeys, createCustomerApiKey, revokeCustomerApiKey, refreshCustomerApiKey } from '../../../api/apiKeysRequests';

const Container = styled.div`
  padding: 24px;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 16px;
  th, td { border-bottom: 1px solid #eee; padding: 12px; text-align: left; }
`;

const SecretBox = styled.pre`
  background: #f8f9fa;
  padding: 12px;
  border-radius: 6px;
  overflow: auto;
`;

export default function ApiKeysPage() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newKeyName, setNewKeyName] = useState('');
  const [justCreatedKey, setJustCreatedKey] = useState(null); // show plaintext only once

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listCustomerApiKeys();
      setKeys(data);
    } catch (e) {
      setError('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onCreate = async () => {
    setError(null);
    try {
      const res = await createCustomerApiKey(newKeyName.trim() || undefined);
      setJustCreatedKey(res.api_key);
      setNewKeyName('');
      await load();
    } catch (e) {
      setError('Failed to create API key');
    }
  };

  const onRevoke = async (id) => {
    setError(null);
    try {
      await revokeCustomerApiKey(id);
      await load();
    } catch (e) {
      setError('Failed to revoke API key');
    }
  };

  const onRefresh = async (id) => {
    setError(null);
    try {
      const res = await refreshCustomerApiKey(id);
      setJustCreatedKey(res.api_key);
      await load();
    } catch (e) {
      setError('Failed to refresh API key');
    }
  };

  return (
    <Container>
      <h2>API Keys</h2>
      <p>Use API keys to authenticate server-to-server requests. Store them securely; they will be shown only once on creation or refresh.</p>

      {error && <div style={{ color: '#c00', marginBottom: 12 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          type="text"
          value={newKeyName}
          onChange={(e) => setNewKeyName(e.target.value)}
          placeholder="Optional key name (e.g., CI/CD)"
          style={{ padding: 8, minWidth: 280 }}
        />
        <button onClick={onCreate} disabled={loading}>Create API Key</button>
      </div>

      {justCreatedKey && (
        <div style={{ marginTop: 16 }}>
          <strong>Copy your new API key now. You won't be able to see it again:</strong>
          <SecretBox>{justCreatedKey}</SecretBox>
        </div>
      )}

      <Table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Created</th>
            <th>Last Used</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {keys.map(k => (
            <tr key={k.id}>
              <td>{k.name || '-'}</td>
              <td>{k.created_at || '-'}</td>
              <td>{k.last_used_at || '-'}</td>
              <td>{k.is_revoked ? 'Revoked' : 'Active'}</td>
              <td>
                <Actions>
                  <button onClick={() => onRefresh(k.id)} disabled={k.is_revoked || loading}>Refresh</button>
                  <button onClick={() => onRevoke(k.id)} disabled={k.is_revoked || loading}>Revoke</button>
                </Actions>
              </td>
            </tr>
          ))}
          {keys.length === 0 && !loading && (
            <tr><td colSpan={5} style={{ color: '#666' }}>No API keys yet.</td></tr>
          )}
        </tbody>
      </Table>
    </Container>
  );
}


